/**
 * useItemPricing
 * Single source of truth for an item's effective pricing in the catalog UI.
 *
 * Encapsulates the cumulative madregot-promo + amount-discount + VAT/pikadon
 * logic so the item dialog and the immersive catalog never diverge. Pass a Ref
 * (or computed) holding the catalog item object; all outputs are reactive.
 *
 * Pricing priority (mirrors the catalog item card):
 *   1. Active madregot promotion (cumulative across the cart). When the item
 *      isn't in the cart yet, a step that is GUARANTEED on the first possible
 *      add (e.g. box-only items whose first step equals one box) is treated as
 *      applied too — no purchasable quantity can get the base price, so
 *      showing it would contradict the catalog card (see anticipatedPromoStep)
 *   2. Amount discount (cumulative by paritMerakez, only when no promo).
 *      Same anticipation rule: when the item isn't in the cart and the first
 *      possible add is guaranteed to land on a tier, that tier is displayed
 *      as applied (see anticipatedDiscountTier)
 *   3. Catalog base price
 */
import { computed, unref } from 'vue'
import { useCartStore } from '../../app/store/cart.js'
import { usePromotionsStore } from '../../app/store/promotions.js'
import { useAmountDiscountsStore } from '../../app/store/amountDiscounts.js'
import { useCatalogStore } from '../../app/store/catalog.js'
import {
  computeCumulativeMadregotApplication,
  getNextStepHint,
  getPromoSteps,
  isMadregotPromo,
} from '../utils/promoCalc.js'
import {
  computeCumulativeAmountDiscountApplication,
  buildDiscountLadderWithBase,
  pickAnticipatedDiscountTier,
  applyDiscountPercent,
} from '../utils/discountCalc.js'
import { VAT_MULTIPLIER, DEPOSIT_COEFF } from '../config/pricingConstants.js'

/**
 * @param {import('vue').Ref<Object|null>|Object} itemRef - catalog item (Ref or plain).
 */
export function useItemPricing(itemRef) {
  const cartStore = useCartStore()
  const promotionsStore = usePromotionsStore()
  const amountDiscountsStore = useAmountDiscountsStore()
  const catalogStore = useCatalogStore()

  const item = computed(() => unref(itemRef) || null)

  // ── Basic item state ──────────────────────────────────────────
  const isOutOfStock = computed(() => item.value?.showOnWeb === 'H')

  const hasValidPrice = computed(() => {
    const p = item.value?.price
    if (p == null || p === '') return false
    const n = parseFloat(p)
    return !isNaN(n) && n > 0
  })

  const cartQty = computed(() => (item.value?.makat ? cartStore.getQty(item.value.makat) : 0))
  const isInCart = computed(() => cartQty.value > 0)

  // onlyBox: 0 / '0' / false / null / undefined => box multiples required
  const isOnlyBox = computed(() => {
    const v = item.value?.onlyBox
    return v == null || v === 0 || v === '0' || v === false
  })

  const boxSize = computed(() => {
    const s = parseInt(item.value?.boxSize, 10)
    return s > 0 ? s : 1
  })

  const hasValidBoxSize = computed(() => parseInt(item.value?.boxSize, 10) > 0)

  // Step for +/- buttons: boxSize for onlyBox items, otherwise 1
  const stepSize = computed(() => (isOnlyBox.value ? boxSize.value : 1))

  // ── Cart snapshot for cumulative computations ─────────────────
  const cartLinesByMakat = computed(() => {
    const result = {}
    for (const line of cartStore.linesArray) {
      result[line.makat] = { qty: line.qty }
    }
    return result
  })

  // ── Cumulative madregot promo ─────────────────────────────────
  const cumulativePromoResult = computed(() => {
    if (!item.value?.makat || !hasValidPrice.value) return null

    const eligiblePromos = promotionsStore.getEligiblePromosForMakat(item.value.makat)
    if (!eligiblePromos || eligiblePromos.length === 0) return null

    // First madregot promo with an applied step (already priority-sorted)
    for (const promo of eligiblePromos) {
      if (!isMadregotPromo(promo)) continue
      const application = computeCumulativeMadregotApplication({
        promo,
        cartLinesByMakat: cartLinesByMakat.value,
      })
      if (application.appliedStep) return { promo, application }
    }

    // Otherwise the first madregot promo (for hints / ladder before it applies)
    const firstMadregot = eligiblePromos.find((p) => isMadregotPromo(p))
    if (firstMadregot) {
      const application = computeCumulativeMadregotApplication({
        promo: firstMadregot,
        cartLinesByMakat: cartLinesByMakat.value,
      })
      return { promo: firstMadregot, application }
    }

    return null
  })

  // Step that hasn't applied yet (item not in cart) but is guaranteed on the
  // very first possible add: previewing the minimum add (one box for box-only
  // items) lands entirely on a step price. In that case the base price is
  // unpurchasable, so the UI presents the step price up front — matching the
  // catalog card instead of flashing base price → promo price on first add.
  const anticipatedPromoStep = computed(() => {
    const r = cumulativePromoResult.value
    if (!r || r.application.appliedStep) return null
    if (cartQty.value > 0) return null
    const makat = item.value.makat
    const preview = computeCumulativeMadregotApplication({
      promo: r.promo,
      cartLinesByMakat: cartLinesByMakat.value,
      pendingDeltaByMakat: { [makat]: stepSize.value },
    })
    if (!preview.appliedStep) return null
    // The whole first add must get the step price (not eaten by a max cap)
    const alloc = preview.allocationsByMakat[makat]
    if (!alloc || alloc.promoQty < stepSize.value) return null
    return preview.appliedStep
  })

  // The step whose price the user effectively pays (or is guaranteed to pay)
  const effectivePromoStep = computed(
    () => cumulativePromoResult.value?.application?.appliedStep || anticipatedPromoStep.value
  )

  const hasPromo = computed(() => effectivePromoStep.value != null)

  const promoIsFirstOrder = computed(() => {
    const r = cumulativePromoResult.value
    if (!r) return false
    return r.application?.meta?.first_order === true || r.promo?.first_order === true
  })

  const hasAnyEligiblePromo = computed(() => {
    if (!item.value?.makat) return false
    const e = promotionsStore.getEligiblePromosForMakat(item.value.makat)
    return e && e.length > 0
  })

  // ── Cumulative amount discount (only when no promo) ───────────
  const discountTiers = computed(() => {
    if (hasAnyEligiblePromo.value) return null
    if (!item.value?.paritMerakez) return null
    return amountDiscountsStore.getDiscountTiers(item.value.paritMerakez)
  })

  // Cart snapshot excluding promo items (discounts never stack with promos)
  const discountCartLinesByMakat = computed(() => {
    const result = {}
    for (const line of cartStore.linesArray) {
      const promos = promotionsStore.getEligiblePromosForMakat(line.makat)
      if (promos && promos.length > 0) continue
      result[line.makat] = { qty: line.qty }
    }
    return result
  })

  const cumulativeDiscountResult = computed(() => {
    if (!discountTiers.value || discountTiers.value.length === 0) return null
    if (!hasValidPrice.value) return null

    return computeCumulativeAmountDiscountApplication({
      paritMerakez: item.value.paritMerakez,
      basePrice: parseFloat(item.value.price),
      discounts: discountTiers.value,
      cartLinesByMakat: discountCartLinesByMakat.value,
      itemsByMakat: catalogStore.itemsByMakat,
    })
  })

  // Tier that hasn't applied yet (item not in cart) but is guaranteed on the
  // very first possible add — the amount-discount mirror of
  // anticipatedPromoStep (e.g. box-only items whose first tier equals one box).
  const anticipatedDiscountTier = computed(() => {
    const r = cumulativeDiscountResult.value
    if (!r || r.isDiscounted) return null
    if (cartQty.value > 0) return null
    return pickAnticipatedDiscountTier({
      discounts: discountTiers.value,
      totalQty: r.totalQty,
      minAdd: stepSize.value,
    })
  })

  // The tier whose price the user effectively pays (or is guaranteed to pay)
  const effectiveDiscountTier = computed(
    () => cumulativeDiscountResult.value?.appliedTier || anticipatedDiscountTier.value
  )

  const hasDiscount = computed(() => effectiveDiscountTier.value != null)

  const discountDisplayInfo = computed(() => {
    if (!hasDiscount.value) return null
    const pct = effectiveDiscountTier.value.discountPercent
    const base = parseFloat(item.value.price)
    return {
      discountPercent: pct,
      totalQty: cumulativeDiscountResult.value.totalQty,
      savings: base - applyDiscountPercent(base, pct),
    }
  })

  // Cumulative qty for the paritMerakez group (drives the discount-ladder fill).
  const discountTotalQty = computed(() => cumulativeDiscountResult.value?.totalQty || 0)

  // Visual amount-discount ladder: [base, ...tiers] each
  // { minQty, discountPercent, unitPriceAfterDiscount, isActive, isBase }.
  // null when the item has no eligible amount-discount tiers.
  const discountLadder = computed(() => {
    if (!discountTiers.value || discountTiers.value.length === 0) return null
    if (!hasValidPrice.value) return null
    return buildDiscountLadderWithBase({
      baseUnitPrice: parseFloat(item.value.price),
      // When a tier is anticipated, highlight it (the state after the first
      // possible add) instead of the unpurchasable base row
      totalQty: discountTotalQty.value + (anticipatedDiscountTier.value ? stepSize.value : 0),
      discounts: discountTiers.value,
    })
  })

  // ── Effective price (promo > discount > catalog) ──────────────
  const effectiveUnitPrice = computed(() => {
    if (hasPromo.value) return effectivePromoStep.value.unitPrice
    if (hasDiscount.value)
      return applyDiscountPercent(
        parseFloat(item.value.price),
        effectiveDiscountTier.value.discountPercent
      )
    if (!hasValidPrice.value) return 0
    return parseFloat(item.value.price)
  })

  // Display info for the dialog price box:
  // { isPromo, unitPrice, step: { description }, promo: { label, first_order, weeklyBenefit } }
  const promoDisplayInfo = computed(() => {
    if (!hasPromo.value) return null
    const r = cumulativePromoResult.value
    return {
      isPromo: true,
      unitPrice: effectivePromoStep.value.unitPrice,
      step: { description: effectivePromoStep.value.description },
      promo: {
        label: r.application.meta.label,
        first_order: promoIsFirstOrder.value,
        weeklyBenefit: r.application.meta.weeklyBenefit === true,
      },
    }
  })

  // ── Hints, ladders, caps ──────────────────────────────────────
  function formatBoxes(amount, bSize) {
    if (!bSize || bSize <= 1) return null
    const boxes = amount / bSize
    if (boxes === 1) return 'מארז אחד'
    if (Number.isInteger(boxes)) return `${boxes} מארזים`
    return `${boxes.toFixed(1)} מארזים`
  }

  const nextStepHint = computed(() => {
    if (!cumulativePromoResult.value?.promo) return null
    const promo = cumulativePromoResult.value.promo
    const totalQty = cumulativePromoResult.value.application.totalQty
    const qtyForHint = totalQty > 0 ? totalQty : 1
    const hint = getNextStepHint(promo, qtyForHint)
    if (!hint) return null
    return {
      unitsNeeded: hint.unitsNeeded,
      nextPrice: hint.nextStep.price,
      description: hint.nextStep.description,
    }
  })

  const nextStepHintWithBoxes = computed(() => {
    if (!nextStepHint.value) return null
    return {
      ...nextStepHint.value,
      boxesText: formatBoxes(nextStepHint.value.unitsNeeded, boxSize.value),
    }
  })

  const maxCapInfo = computed(() => {
    if (!cumulativePromoResult.value?.application) return null
    const app = cumulativePromoResult.value.application
    if (!app.maxQty) return null
    const totalQty = app.totalQty
    const remaining = Math.max(0, app.maxQty - totalQty)
    return {
      max: app.maxQty,
      totalQty,
      remaining,
      isAtMax: remaining === 0,
      isOverMax: app.meta.isOverMax,
      overMaxUnits: app.meta.overMaxUnits,
    }
  })

  const promoLadder = computed(() => {
    if (!cumulativePromoResult.value?.promo) return null
    const promo = cumulativePromoResult.value.promo
    const steps = getPromoSteps(promo)
    if (steps.length === 0) return null
    const appliedStepKey = cumulativePromoResult.value.application?.appliedStep?.stepKey || null
    const bSize = boxSize.value
    return steps.map((step) => ({
      stepKey: step.stepKey,
      amount: step.amount,
      price: step.price,
      priceText: step.price.toFixed(2),
      description: step.description,
      boxesText: formatBoxes(step.amount, bSize),
      isActive: step.stepKey === appliedStepKey,
    }))
  })

  // ── Formatted display values ──────────────────────────────────
  const formattedPrice = computed(() =>
    hasValidPrice.value ? effectiveUnitPrice.value.toFixed(2) : '—'
  )

  const originalPrice = computed(() =>
    hasValidPrice.value ? parseFloat(item.value.price).toFixed(2) : null
  )

  const hasPikadon = computed(() => {
    const p = parseFloat(item.value?.pikadon)
    return !isNaN(p) && p > 0
  })

  const pikadonValue = computed(() => {
    if (!hasPikadon.value || cartQty.value === 0) return null
    const p = parseFloat(item.value.pikadon)
    return (p * cartQty.value * DEPOSIT_COEFF).toFixed(2)
  })

  const lineTotal = computed(() => {
    if (!hasValidPrice.value || cartQty.value === 0) return null
    const priceTotal = effectiveUnitPrice.value * cartQty.value
    if (hasPikadon.value) {
      const p = parseFloat(item.value.pikadon)
      return (priceTotal + p * cartQty.value * DEPOSIT_COEFF).toFixed(2)
    }
    return priceTotal.toFixed(2)
  })

  const lineTotalWithVat = computed(() => {
    if (!lineTotal.value) return null
    return (parseFloat(lineTotal.value) * VAT_MULTIPLIER).toFixed(2)
  })

  return {
    item,
    isOutOfStock,
    hasValidPrice,
    cartQty,
    isInCart,
    isOnlyBox,
    boxSize,
    hasValidBoxSize,
    stepSize,
    cumulativePromoResult,
    hasPromo,
    promoDisplayInfo,
    promoIsFirstOrder,
    hasAnyEligiblePromo,
    discountTiers,
    cumulativeDiscountResult,
    hasDiscount,
    discountDisplayInfo,
    discountTotalQty,
    discountLadder,
    effectiveUnitPrice,
    nextStepHint,
    nextStepHintWithBoxes,
    maxCapInfo,
    promoLadder,
    formattedPrice,
    originalPrice,
    hasPikadon,
    pikadonValue,
    lineTotal,
    lineTotalWithVat,
    formatBoxes,
  }
}
